import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Play, ArrowLeft, Crown, Lock, Clock, ChevronDown,
  Film, Tv, Share2, CheckCircle, X, Pause, Volume2, VolumeX, Maximize
} from 'lucide-react';
import axios from 'axios';
import Hls from 'hls.js';
import VODFooter from '../components/VODFooter';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CLOUDFLARE_CUSTOMER_CODE = 'xg4xjbetrvqn62vp';

const VODSeriesDetail = () => {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [customer, setCustomer] = useState(null);
  const [showTrailerModal, setShowTrailerModal] = useState(false);

  useEffect(() => {
    const customerData = localStorage.getItem('livestreamCustomer');
    if (customerData) {
      try {
        setCustomer(JSON.parse(customerData));
      } catch (e) {}
    }
    
    fetchSeries();
  }, [seriesId]);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('livestreamToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API}/api/vod/series/${seriesId}`, { headers });
      setSeries(res.data);
      
      // Set first available season
      if (res.data.seasons?.length > 0) {
        setSelectedSeason(res.data.seasons[0].season_number);
      }
    } catch (err) {
      console.error('Error fetching series:', err);
      setError(err.response?.data?.detail || 'Failed to load series');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: series.title, url });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  const currentSeasonEpisodes = series?.seasons?.find(s => s.season_number === selectedSeason)?.episodes || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex flex-col items-center justify-center text-white p-4">
        <p className="text-red-400 mb-4">{error}</p>
        <Button onClick={() => navigate('/vod/browse')} variant="outline">
          Back to Browse
        </Button>
      </div>
    );
  }

  if (!series) return null;

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <Helmet>
        <title>{series?.title ? `${series.title} | LTV VOD` : 'Series | LTV VOD'}</title>
        <meta name="description" content={series?.description ? series.description.substring(0, 160) : 'Watch this series on LTV VOD.'} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Header */}
      <header className="bg-[#0d0d0d] border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/vod" className="flex items-center gap-2">
              <img 
                src="/ltv-icon.png" 
                alt="LTV Logo" 
                className="w-8 h-8 object-contain brightness-0 invert"
              />
              <span className="font-bold text-base sm:text-lg">LTV VOD</span>
            </Link>
            <nav className="hidden md:flex items-center gap-4 text-sm text-gray-400">
              <Link to="/vod/browse" className="hover:text-white transition-colors">BROWSE</Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {!customer ? (
              <>
                <Button 
                  onClick={() => navigate('/vod/membership')}
                  className="bg-red-600 hover:bg-red-500 text-white font-semibold"
                >
                  SUBSCRIBE
                </Button>
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/live/login?from=vod&redirect=/vod/series/' + seriesId)}
                  className="text-gray-300 hover:text-white"
                >
                  SIGN IN
                </Button>
              </>
            ) : (
              <Button 
                variant="ghost"
                onClick={() => navigate('/live/account?from=vod')}
                className="text-gray-300 hover:text-white"
              >
                My Account
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-start">
            {/* Left - Info */}
            <div className="flex-1 order-2 lg:order-1 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3" data-testid="series-title">
                {series.title}
              </h1>
              
              <p className="text-gray-400 mb-4">
                {series.total_episodes} Episode{series.total_episodes !== 1 ? 's' : ''}
              </p>

              <p className="text-gray-300 text-base lg:text-lg mb-8 max-w-xl leading-relaxed">
                {series.description}
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                {series.has_full_access ? (
                  <Button 
                    className="bg-green-600 hover:bg-green-500 text-white font-semibold px-8"
                    onClick={() => currentSeasonEpisodes[0] && navigate(`/vod/watch/${currentSeasonEpisodes[0].id}`)}
                  >
                    <Play className="h-4 w-4 mr-2" fill="white" />
                    Play
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate('/vod/membership')}
                    className="bg-red-600 hover:bg-red-500 text-white font-semibold px-8"
                  >
                    SUBSCRIBE
                  </Button>
                )}
                
                {series.trailer_url && (
                  <Button 
                    variant="outline"
                    className="border-gray-600 text-white hover:bg-white/10"
                    onClick={() => setShowTrailerModal(true)}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Trailer
                  </Button>
                )}
                
                <Button 
                  variant="outline"
                  className="border-gray-600 text-white hover:bg-white/10"
                  onClick={handleShare}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>

              {/* Tier Badge */}
              {series.tier === 'premium' && (
                <div className="mt-6">
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500">
                    <Crown className="h-3 w-3 mr-1" />
                    Premium Series
                  </Badge>
                </div>
              )}
            </div>

            {/* Right - Poster */}
            <div className="w-full max-w-md lg:max-w-lg order-1 lg:order-2">
              <div className="aspect-video rounded-xl overflow-hidden shadow-2xl bg-gray-800">
                {series.thumbnail_url ? (
                  <img 
                    src={series.thumbnail_url} 
                    alt={series.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900 to-gray-900">
                    <Tv className="h-20 w-20 text-gray-600" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Episodes Section */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Season Selector Row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              {/* Season Dropdown */}
              <div className="relative">
                <select
                  value={selectedSeason}
                  onChange={(e) => setSelectedSeason(Number(e.target.value))}
                  className="appearance-none bg-gray-800 text-white px-5 py-3 pr-10 rounded-lg font-medium cursor-pointer hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="season-selector"
                >
                  {series.seasons?.map((season) => (
                    <option key={season.season_number} value={season.season_number}>
                      Season {season.season_number}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  className="appearance-none bg-gray-800 text-white px-5 py-3 pr-10 rounded-lg font-medium cursor-pointer hover:bg-gray-700 transition-colors focus:outline-none"
                >
                  <option>Sort by...</option>
                  <option>Episode Number</option>
                  <option>Newest First</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Episode Count */}
            <p className="text-gray-400">
              {currentSeasonEpisodes.length} Episode{currentSeasonEpisodes.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Episode Grid */}
          {currentSeasonEpisodes.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="episodes-grid">
              {currentSeasonEpisodes.map((episode, idx) => (
                <EpisodeCard 
                  key={episode.id}
                  episode={episode}
                  index={idx}
                  formatDuration={formatDuration}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500">
              <Film className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p>No episodes available for this season</p>
            </div>
          )}
        </div>
      </section>

      {/* Back Button - Fixed */}
      <button
        onClick={() => navigate('/vod/browse')}
        className="fixed bottom-6 left-6 bg-gray-800/90 backdrop-blur-sm hover:bg-gray-700 text-white px-4 py-2 rounded-full flex items-center gap-2 transition-colors shadow-lg"
        data-testid="back-to-browse-btn"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* Trailer Modal with HLS Player */}
      {showTrailerModal && series.trailer_url && (
        <TrailerModal 
          videoId={series.trailer_url}
          title={series.title}
          onClose={() => setShowTrailerModal(false)}
        />
      )}
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

// Trailer Modal Component with HLS.js player
const TrailerModal = ({ videoId, title, onClose }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoId) return;

    // Use videodelivery.net which is the public CDN domain
    const hlsUrl = `https://videodelivery.net/${videoId}/manifest/video.m3u8`;

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        xhrSetup: function(xhr) {
          xhr.withCredentials = false;
        }
      });
      
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(e => console.log('Autoplay prevented:', e));
        setIsPlaying(true);
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          // Try customer subdomain as fallback
          const fallbackUrl = `https://customer-${CLOUDFLARE_CUSTOMER_CODE}.cloudflarestream.com/${videoId}/manifest/video.m3u8`;
          hls.loadSource(fallbackUrl);
        }
      });
      
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      video.src = hlsUrl;
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(e => console.log('Autoplay prevented:', e));
        setIsPlaying(true);
      });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, [videoId]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 z-10 p-2 text-white/70 hover:text-white transition-colors"
        >
          <X className="h-8 w-8" />
        </button>
        
        <div className="aspect-video bg-black rounded-xl overflow-hidden shadow-2xl relative group">
          {error ? (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                className="w-full h-full object-contain"
                playsInline
                controls={false}
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                onClick={togglePlay}
              />
              
              {/* Video Controls */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={togglePlay} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                      {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" fill="white" />}
                    </button>
                    <button onClick={toggleMute} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                      {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                    </button>
                  </div>
                  <button onClick={toggleFullscreen} className="p-2 hover:bg-white/20 rounded-full transition-colors">
                    <Maximize className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        
        <p className="text-center text-gray-500 text-sm mt-4">
          {title} - Trailer
        </p>
      </div>
    </div>
  );
};

// Episode Card Component
const EpisodeCard = ({ episode, index, formatDuration }) => {
  const navigate = useNavigate();
  const episodeNumber = episode.episode_number || (index + 1);
  
  const handleClick = () => {
    if (episode.has_access) {
      navigate(`/vod/watch/${episode.id}?autoplay=true`);
    } else {
      navigate(`/vod/video/${episode.id}?autoplay=true`);
    }
  };

  return (
    <div 
      onClick={handleClick}
      className="group cursor-pointer"
      data-testid={`episode-${episode.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-800 mb-3">
        {episode.thumbnail_url ? (
          <img 
            src={episode.thumbnail_url} 
            alt={episode.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            <Play className="h-10 w-10 text-gray-600" />
          </div>
        )}

        {/* Lock Icon for locked content */}
        {!episode.has_access && (
          <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/70 flex items-center justify-center">
            <Lock className="h-4 w-4 text-white" />
          </div>
        )}

        {/* Free Preview Badge */}
        {episode.free_preview && (
          <div className="absolute top-2 left-2 px-2 py-1 rounded bg-green-600 text-white text-xs font-medium">
            FREE
          </div>
        )}

        {/* Checkmark for owned content (non-free) */}
        {episode.has_access && !episode.free_preview && (
          <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
            <CheckCircle className="h-4 w-4 text-white" />
          </div>
        )}

        {/* Duration */}
        {episode.duration > 0 && (
          <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded font-medium">
            {formatDuration(episode.duration)}
          </div>
        )}

        {/* Hover Play Overlay */}
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Play className="h-7 w-7 text-white ml-1" fill="white" />
          </div>
        </div>
      </div>

      {/* Title */}
      <h3 className="font-semibold text-sm lg:text-base mb-1 line-clamp-2 group-hover:text-purple-400 transition-colors">
        {episodeNumber}. {episode.title}
      </h3>
      
      {/* Episode Label */}
      <p className="text-xs text-gray-500">
        Episode {episodeNumber}
      </p>
    </div>
  );
};

export default VODSeriesDetail;
